import { Directive, HostBinding, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, NavigationExtras, RouterLinkWithHref } from '@angular/router';

import { RoutingStateService } from '../../../core/common/services/routing-state.service';
import { NavigationLink } from '../../components/navigation-item/entities/navigation-link.entity';

@Directive({
    selector: '[shNavigationLink]',
    providers: [{ provide: RouterLinkWithHref, useExisting: NavigationLinkDirective }], // https://github.com/angular/angular/issues/8580
    standalone: true
})
export class NavigationLinkDirective extends RouterLinkWithHref implements OnChanges {
    @HostBinding('attr.rel') relAttr = '';

    private isExternalLink = false;
    private link: NavigationLink;

    @Input() set shNavigationLink(navigationLink: NavigationLink) {
        this.link = navigationLink;
        this.isExternalLink = this.isLinkExternal(navigationLink);
        this.init(navigationLink);
    }

    private readonly routingStateService: RoutingStateService = inject(RoutingStateService);

    ngOnChanges(changes: SimpleChanges): any {
        if (this.isExternalLink) {
            this.href = changes.shNavigationLink.currentValue.route;
        } else {
            return super.ngOnChanges(changes);
        }
    }

    private init(navigationLink: NavigationLink): void {
        if (this.isExternalLink) {
            this.setExternalLinkProperties();
        } else if (navigationLink.extras?.relativeTo) {
            this.setRelativeRouteProperties(navigationLink);
        } else {
            this.setRouteProperties(navigationLink);
        }

        this.extractTarget(navigationLink);

        this.extractExtras(navigationLink);
    }

    private setExternalLinkProperties(): void {
        this.ngOnDestroy(); // Hack: we need to unsubscribe the route listener
        this.relAttr = 'noopener nofollow';
    }

    private setRelativeRouteProperties(navigationLink: NavigationLink): void {
        if (!navigationLink.extras?.relativeTo) {
            return;
        }

        // We're in a router-outlet context

        // Check if we're trying to navigate to a sibling route or a totally different route
        const routeSegments: string[] = navigationLink.route.split('/');
        const parentSegment: ActivatedRoute | undefined = this.getNonEmptyParentSegment(
            navigationLink.extras.relativeTo
        );
        const parentSegmentUrl: string | undefined = parentSegment?.snapshot.url.toString();

        if (parentSegmentUrl && routeSegments.slice(1, -1).join('/').startsWith(parentSegmentUrl)) {
            // We're navigating to a sibling route. Strip parts that are already present in the current route
            // from the target route. (Navigating upwards using relative paths doesn't resolve for some reason).
            const commonIndex: number = routeSegments.indexOf(parentSegmentUrl);
            this.routerLink = [
                {
                    outlets: {
                        primary: navigationLink.route.split('/').slice(commonIndex + 1),
                        preview: null
                    }
                }
            ];
        } else {
            // We're navigating to a totally different route. Operate like usual
            // Set relativeTo undefined, so we default back to the baseUrl.
            this.routerLink = [navigationLink.route];
            navigationLink.extras.relativeTo = undefined;
        }
    }

    private setRouteProperties(navigationLink: NavigationLink): void {
        // We're not in a router-outlet context, normal operations
        this.routerLink = [navigationLink.route];
    }

    private extractTarget(navigationLink: NavigationLink): void {
        this.target = navigationLink.target;
    }

    private extractExtras(navigationLink: NavigationLink): void {
        const extras: NavigationExtras | undefined = navigationLink.extras;

        if (!extras) {
            return;
        }

        if (extras.fragment) {
            this.fragment = extras.fragment;
        }

        if (extras.preserveFragment) {
            this.preserveFragment = extras.preserveFragment;
        }

        if (extras.queryParams) {
            this.queryParams = extras.queryParams;
        }

        if (extras.queryParamsHandling) {
            this.queryParamsHandling = extras.queryParamsHandling;
        }

        if (extras.replaceUrl) {
            this.replaceUrl = extras.replaceUrl;
        }

        if (extras.skipLocationChange) {
            this.skipLocationChange = extras.skipLocationChange;
        }

        if (extras.state) {
            this.state = extras.state;
        }

        if (extras.relativeTo) {
            this.relativeTo = extras.relativeTo;
        }
    }

    public onClick(button: number, ctrlKey: boolean, shiftKey: boolean, altKey: boolean, metaKey: boolean): boolean {
        this.routingStateService.setRoutingState(this.state ?? {});

        if (this.isExternalLink) {
            return true;
        }

        return super.onClick.apply(this, [button, ctrlKey, shiftKey, altKey, metaKey]);
    }

    private isLinkExternal(navigationLink: NavigationLink): boolean {
        return !!navigationLink.route.startsWith('http');
    }

    private getNonEmptyParentSegment(activatedRoute: ActivatedRoute): ActivatedRoute | undefined {
        let segment: ActivatedRoute = activatedRoute;

        if (!segment) {
            return undefined;
        }

        while (!segment.snapshot.url.toString()) {
            if (!segment.parent) {
                return undefined;
            }
            segment = segment.parent!;
        }

        return segment;
    }
}
